import React from "react";
import "./Search.scss";
import Strings from "../../config/Strings";
import {Form} from "react-bootstrap";

//age quick selection, used by the search and the list of all series; the first entry stands for "all ages"
const AGE_SELECTIONS = [
    {key: 'ALL', ageMin: "", ageMax: ""},
    {key: '0-2', ageMin: 0, ageMax: 2},
    {key: '3-5', ageMin: 3, ageMax: 5},
    {key: '6-8', ageMin: 6, ageMax: 8},
    {key: '9-12', ageMin: 9, ageMax: 12},
    {key: '13+', ageMin: 13, ageMax: ""},
];

//the chips of the age quick selection without "all"
const AGE_CHIPS = AGE_SELECTIONS.filter(selection => selection.key !== 'ALL');

//a profile whose age range starts at 0 and reaches at least this age gets "all" ages as preselection
const PROFILE_ALL_AGES_MAX = 12;

export const ALL_AGES = {ageMin: "", ageMax: ""};

//the profile selected on the search page or the list of all series, it's the default for both pages
const SELECTED_PROFILE_STORAGE_KEY = "search.selectedProfileId";

function hasValue(value) {
    return value !== "" && value != null;
}

//indices of the age chips covered by the age filter; only neighbouring chips can be selected together,
//so the age filter is always the range from the first to the last selected chip
function getSelectedAgeChipIndices(ageMin, ageMax) {
    if (!hasValue(ageMin) && !hasValue(ageMax)) {
        return [];
    }
    const lower = hasValue(ageMin) ? Number(ageMin) : -Infinity;
    const upper = hasValue(ageMax) ? Number(ageMax) : Infinity;
    return AGE_CHIPS
            .map((chip, index) => ({chip, index}))
            .filter(({chip}) => chip.ageMin >= lower && (chip.ageMax === "" ? upper === Infinity : chip.ageMax <= upper))
            .map(({index}) => index);
}

//the age filter for the chips from first to last, selecting all chips is the same as "all" ages
function getAgeFilterOfChips(first, last) {
    if (first === 0 && last === AGE_CHIPS.length - 1) {
        return ALL_AGES;
    }
    return {ageMin: AGE_CHIPS[first].ageMin, ageMax: AGE_CHIPS[last].ageMax};
}

//a neighbouring chip extends the selection, an outer selected chip is removed from it;
//every other chip is selected on its own, so the selected chips never have a gap
function getAgeFilterAfterChipClick(ageMin, ageMax, index) {
    const selected = getSelectedAgeChipIndices(ageMin, ageMax);
    const first = selected[0];
    const last = selected[selected.length - 1];
    if (selected.length === 0) {
        return getAgeFilterOfChips(index, index);
    }
    if (!selected.includes(index)) {
        if (index === first - 1) {
            return getAgeFilterOfChips(index, last);
        }
        if (index === last + 1) {
            return getAgeFilterOfChips(first, index);
        }
        return getAgeFilterOfChips(index, index);
    }
    if (selected.length === 1) {
        return ALL_AGES;
    }
    if (index === first) {
        return getAgeFilterOfChips(first + 1, last);
    }
    if (index === last) {
        return getAgeFilterOfChips(first, last - 1);
    }
    return getAgeFilterOfChips(index, index);
}

function getAgeSelectionLabel(selection) {
    if (selection.ageMax === "") {
        return Strings.SEARCH_AGE_FROM.replace("{0}", selection.ageMin);
    }
    return Strings.SEARCH_AGE_RANGE.replace("{0}", selection.ageMin).replace("{1}", selection.ageMax);
}

//the age filter preselected for a profile: "all" ages for a profile from 0 up to 12 years or more,
//otherwise the age chips overlapping the age range of the profile
export function getAgeFilterOfProfile(profile) {
    const profileAgeMin = profile.ageRange?.min;
    const profileAgeMax = profile.ageRange?.max;
    if (profileAgeMin == null && profileAgeMax == null) {
        return ALL_AGES;
    }
    const lower = profileAgeMin ?? 0;
    const upper = profileAgeMax ?? Infinity;
    if (lower <= 0 && upper >= PROFILE_ALL_AGES_MAX) {
        return ALL_AGES;
    }
    const overlappingChips = AGE_CHIPS
            .map((chip, index) => ({chip, index}))
            .filter(({chip}) => chip.ageMin <= upper && (chip.ageMax === "" || chip.ageMax >= lower))
            .map(({index}) => index);
    if (overlappingChips.length === 0) {
        return ALL_AGES;
    }
    return getAgeFilterOfChips(overlappingChips[0], overlappingChips[overlappingChips.length - 1]);
}

//the profile selected before on the search page or the list of all series, otherwise the first profile
export function getDefaultProfile(profiles) {
    let storedProfileId = null;
    try {
        storedProfileId = localStorage.getItem(SELECTED_PROFILE_STORAGE_KEY);
    } catch (e) {
        //the stored selection is only a convenience
    }
    return profiles.find(profile => String(profile.id) === storedProfileId) || profiles[0];
}

export function storeSelectedProfile(profileId) {
    try {
        localStorage.setItem(SELECTED_PROFILE_STORAGE_KEY, String(profileId));
    } catch (e) {
        //the stored selection is only a convenience
    }
}

function renderChip(key, label, selected, onClick) {
    return (
        <button type="button" key={key} className={"search-filter-chip" + (selected ? " selected" : "")}
                aria-pressed={selected} onClick={onClick}>
            {label}
        </button>
    );
}

//"Für wen suchst du?" with the profile selection (only for logged in users) and the age chips
export const AgeQuickFilter = ({profiles, selectedProfile, showProfiles, ageMin, ageMax, onSelectProfile, onAgeFilterChange}) => {
    const selectedAgeChips = getSelectedAgeChipIndices(ageMin, ageMax);
    const allAgesSelected = !hasValue(ageMin) && !hasValue(ageMax);
    return (
        <div className="search-quick-filter-container">
            {showProfiles && profiles && profiles.length > 0 &&
                <div className="search-quick-filter-header">
                    <div className="search-age-question">{Strings.SEARCH_AGE_QUESTION}</div>
                    <div className="search-profile-select-container">
                        <label htmlFor="search-profile-select">{Strings.SEARCH_PROFILE_LABEL}</label>
                        <Form.Select id="search-profile-select" className="search-profile-select"
                                     value={selectedProfile ?? ""}
                                     onChange={(e) => onSelectProfile(e.target.value)}>
                            {profiles.map(profile => (
                                <option value={profile.id} key={profile.id}>{profile.name}</option>
                            ))}
                        </Form.Select>
                    </div>
                </div>
            }
            <div className="search-filter-chips" role="group" aria-label={Strings.ADVANCED_SEARCH_AGE}>
                {renderChip('ALL', Strings.SEARCH_FILTER_ALL, allAgesSelected, () => onAgeFilterChange(ALL_AGES))}
                {AGE_CHIPS.map((chip, index) => renderChip(chip.key, getAgeSelectionLabel(chip),
                        selectedAgeChips.includes(index), () => onAgeFilterChange(getAgeFilterAfterChipClick(ageMin, ageMax, index))))}
            </div>
        </div>
    );
};
